import * as aq from 'arquero';
import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import { type AstInput, extractLambdaBody, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { compileProjectorLambda, withTable } from '../../runtime/arquero.ts';
import { astArg, filterPredicateArg } from '../../schema/ast-codec.ts';
import { exprColRefs } from '../../schema/check.ts';
import { passThroughSchema } from '../../schema/infer.ts';
import { expectLambda, type Value } from '../../value.ts';
import { resolveDsArg } from './helpers.ts';

/**
 * `(filter ds <fn>)` — keep rows where the predicate function returns
 * truthy. `<fn>` is a function value (typically a `#(…)` reader macro
 * desugared to `(fn [%] body)`), invoked once per row with the row map
 * as its sole argument. Keyword col-refs inside the body resolve
 * against the row via `(:col %)` (which desugars to `(col % :col)` and
 * routes through `col`'s expression-compile path).
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - Second arg not a `LambdaValue` → `TypeMismatchError`.
 *   - Lambda arity ≠ 1 → `ProjectorArityError`.
 *   - A keyword col-ref names a missing column → `UnknownColumnRefError`
 *     (surfaced by `col`'s compile-time check).
 *
 * A `LazyDataset` input is appended as a `filter` plan node by the
 * dataset-op wrapper (see `evalDatasetOp`); materialised inputs run
 * arquero's `.filter` immediately. Cross-dataset `(col other-ds …)`
 * references and inline `(query …)` sub-query operands in the predicate
 * are already inlined to literal vectors by `inlineSetRefs` (run per-form
 * in `evalScript` before any op dispatch), so apply sees only literals.
 */
export const filter = z
  .object({
    name: z.literal('filter'),
    params: z.tuple([astArg('<dataset>'), filterPredicateArg()]),
  })
  .describe('Keep rows where the predicate function returns truthy. Predicate is a `#(…)` row function.')
  .register(opMeta, {
    // Special evaluation: the function arg evaluates to a `LambdaValue`
    // whose body must be compiled with row-identity subst, not invoked
    // through the normal call protocol — so we evaluate both positional
    // args by hand via `ctx.evalNode`.
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> wells (filter #(> (:production %) 100)))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [dsAst, fnAst] = data.params as readonly [AstInput, AstInput];
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(filter ds <fn>)', ctx);

      const { value: fnValue } = await ctx.evalNode(fnAst, ctx.env, paramPath(ctx.path, 1));
      const lambda = expectLambda(fnValue as Value, '(filter ds <fn>)', nodePos(ctx.spans, paramPath(ctx.path, 1)));
      const rowFn = await compileProjectorLambda(lambda, 1, ctx.ops, ctx.identities, ctx.spans, paramPath(ctx.path, 1));

      const filtered = withTable(concrete, (t) => t.filter(aq.escape(rowFn)));
      return { value: filtered, env: ctx.env };
    },
    dataset: {
      // Row-level filter: columns unchanged.
      propagateSchema: passThroughSchema,
      // Predicate-body col refs drive unknown-ref diagnostics and
      // per-cell dispatch — a predicate referencing a `nested-dataset`
      // column lifts `filter` per-cell automatically.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const lambdaAst = (data as { readonly params?: readonly unknown[] }).params?.[1];
        const extracted = extractLambdaBody(lambdaAst);
        if (extracted === undefined) {
          return [];
        }
        const [body, lambdaToBody] = extracted;
        return exprColRefs(body, `${basePath}/params/1/${lambdaToBody}`);
      },
    },
  });
